import { Alert, Button, CircularProgress, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import { ChangeEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { projectSlice } from '../../../redux/ProjectSlice';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { useNavigate } from 'react-router-dom';
import { projectsApi } from '../../../api/projectsApi';
import { formatCodeInput, isValidShareCode } from '../../../utils/codeValidation';
import { ApplicationActions } from '../../../utils/constants';
import { Project } from '../../Projects/projects';
import { parseProjectFromApiResponse } from '../../../utils/projectApiMetadata';
import HelpPopover from '../../HelpPopover';
import { isBrowserOffline } from '../../../utils/networkStatus';

interface OpenShareCodeDialogProps {
  onClose: () => void;
  open: boolean;
}

const OpenShareCodeDialog = ({ onClose }: OpenShareCodeDialogProps) => {
  const [shareCode, setShareCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleInput = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const formatted = formatCodeInput(event.target.value);
    setShareCode(formatted);
    setError(''); // Clear error when user types
  };

  const handleCancelShareCode = () => {
    setShareCode('');
    setError('');
    onClose();
  };

  const handleOpenShareCode = async () => {
    // Validate share code format
    if (!isValidShareCode(shareCode)) {
      setError(t('openShareCodeDialog.error.invalid-format', 'Share code must be 6 uppercase letters and numbers (e.g., ABC123)'));
      return;
    }

    if (isBrowserOffline()) {
      setError(t('openShareCodeDialog.error.offline', 'You appear to be offline. Connect to the internet to open share codes.'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Fetch project from API
      const response = await projectsApi.get(shareCode);

      const isTemplate = response.is_template;

      if (isTemplate) {
        const { PROJECT_NAME } = ApplicationActions.Dialogs;
        onClose();
        dispatch(
          applicationSlice.actions.onOpenDialog({
            ...PROJECT_NAME,
            options: {
              title: 'projectNameDialog.new.title',
              rapidRobotCode: shareCode,
            },
          })
        );
        navigate('/');
      } else {
        const fetchedProject: Project = parseProjectFromApiResponse(response);

        // Set viewer access level and read-only mode
        const projectToLoad: Project = {
          ...fetchedProject,
          shareCode: shareCode,
          accessLevel: 'viewer',
          readOnly: true,
          // Don't include the PIN - viewer doesn't have it
          pin: undefined,
        };

        // Update Redux state with access level
        dispatch(
          projectSlice.actions.onSetAccessLevel({
            projectId: projectToLoad.id,
            level: 'viewer',
          })
        );

        // Load the project
        dispatch(projectSlice.actions.onLoadProject(projectToLoad));

        // Navigate to editor
        navigate(`/editor/${projectToLoad.id}`);

        // Close dialog
        onClose();
      }
    } catch (err: unknown) {
      console.error('Failed to open share code:', err);

      if (typeof err === 'object' && err !== null && 'status' in err && err.status === 404) {
        setError(t('openShareCodeDialog.error.not-found', 'Share code not found. Please check the code and try again.'));
      } else {
        setError(t('openShareCodeDialog.error.general', 'Failed to open share code. Please check your connection and try again.'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <DialogTitle textAlign="center">
        <Trans i18nKey={'openShareCodeDialog.title'}>Open Share Code</Trans>
      </DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          direction="column"
          spacing={2}
          marginTop={1}
          alignItems={'center'}
          onSubmit={(e) => {
            e.preventDefault();
            void handleOpenShareCode();
          }}
        >
          <Typography>
            <Trans i18nKey={'openShareCodeDialog.share-code-explanation'}>If you have a share code, enter it below to access:</Trans>
          </Typography>
          <Stack direction={'row'} spacing={1} alignItems={'center'}>
            <TextField
              id="share-code"
              placeholder={t('openShareCodeDialog.input.placeholder', 'ABC123')}
              value={shareCode}
              onChange={handleInput}
              disabled={isLoading}
              slotProps={{ inputLabel: { shrink: true } }}
              inputProps={{ maxLength: 6 }}
              error={!!error}
            />
            <HelpPopover
              id="open-share-code-popover"
              ariaLabel={t('aria-label.more-info', 'more information')}
              text={t('openShareCode.share-code-info', 'Enter 6-character share code')}
            />
          </Stack>

          {/* Show error message */}
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" display="flex" justifyContent="space-between" width={'100%'}>
            <Button type="button" onClick={handleCancelShareCode} disabled={isLoading}>
              <Trans i18nKey="openShareCodeDialog.button.cancel">Cancel</Trans>
            </Button>

            <Button type="submit" disabled={isLoading || !shareCode} startIcon={isLoading ? <CircularProgress size={20} /> : null}>
              {isLoading ? (
                <Trans i18nKey="openShareCodeDialog.button.opening">Opening...</Trans>
              ) : (
                <Trans i18nKey="openShareCodeDialog.button.open">Open</Trans>
              )}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </>
  );
};

export default OpenShareCodeDialog;
